import React, { ComponentType } from 'react';
import {
  AuthServiceContext,
  authServiceContext,
  AuthStateContext,
  authStateContext,
} from './AuthServiceProvider';

export interface WithAuthServiceProps {
  authService: AuthServiceContext;
}

/**
 * Take a component arg that requires an `authService` property and returns a new components that is
 * automatically given 'authService' from context. The new component will no longer require an
 * `authService` property.
 *
 * This must be nested beneath an <AuthServiceProvider /> as this uses its configuration.
 *
 * @param Component
 */
export const withAuthService = <P extends object>(
  Component: ComponentType<P & WithAuthServiceProps>,
) =>
  class WithAuthService extends React.Component<Omit<P, 'authService'>> {
    render() {
      // need to cast because while we know that props is just P without authService,
      // TypeScript can't figure it out because WithAuthServiceProps could have more
      // stuff in it besides authService (even though it doesn't)
      const props = this.props as P;
      return (
        <authServiceContext.Consumer>
          {(authService) => {
            if (authService instanceof Error) {
              throw authService;
            }
            return <Component authService={authService} {...props} />;
          }}
        </authServiceContext.Consumer>
      );
    }
  };

export interface WithAuthStateProps {
  authState: AuthStateContext;
}

/**
 * Take a component arg that requires an `authState` property and returns a new components that is
 * automatically given 'authState' from context. The new component will no longer require an
 * `authState` property.
 *
 * This must be nested beneath an <AuthServiceProvider /> as this uses its configuration.
 *
 * @param Component
 */
export const withAuthState = <P extends object>(
  Component: ComponentType<P & WithAuthStateProps>,
) =>
  class WithAuthState extends React.Component<Omit<P, 'authState'>> {
    render() {
      // need to cast because while we know that props is just P without authService,
      // TypeScript can't figure it out because WithAuthServiceProps could have more
      // stuff in it besides authService (even though it doesn't)
      const props = this.props as P;
      return (
        <authStateContext.Consumer>
          {(authState) => {
            return <Component authState={authState} {...props} />;
          }}
        </authStateContext.Consumer>
      );
    }
  };
